/**
 * The JS half of the currency formatting engine — the mirror of
 * App\Services\CurrencyFormatService.
 *
 * Both halves read the same config (config/currency-locales.php), which reaches
 * this side through the `context_currency` global injected by the app layout.
 * Everything money-shaped funnels through helpers/formatService.js, which
 * delegates here, so call sites localise without changing.
 *
 * Never call Intl.NumberFormat without an explicit locale: an implicit locale
 * resolves to the BROWSER's, which would mean two people looking at the same
 * budget see different figures.
 */

// Reproduces the historical output (Anglo grouping, the frozen M/k tile
// suffixes) so a page without the layout global, or a component rendered in
// isolation, degrades to today's behaviour instead of throwing.
const FALLBACK = {
    code: null,
    locale: 'en-US',
    numberingSystem: 'latn',
    symbol: null,
    indicator: { glyph: null, position: 'before', space: false },
    compact: { suffixes: { 1000000: 'M', 1000: 'k' }, maximumFractionDigits: null },
};

export const resolveContext = () => {
    const injected = typeof window !== 'undefined' ? window.context_currency : null;

    if (!injected) {
        return FALLBACK;
    }

    return {
        ...FALLBACK,
        ...injected,
        indicator: { ...FALLBACK.indicator, ...(injected.indicator || {}) },
        compact: { ...FALLBACK.compact, ...(injected.compact || {}) },
    };
};

export const locale = () => resolveContext().locale || FALLBACK.locale;

// The numbering system is appended to every locale, never configured per entry:
// ten of the currencies resolve to locales that would otherwise emit Arabic-Indic
// or Devanagari numerals.
const icuLocale = (tag) => `${tag}-u-nu-${resolveContext().numberingSystem || 'latn'}`;

const formatterCache = new Map();

/**
 * Constructing an Intl.NumberFormat is expensive and grids format per cell, so
 * they are cached by everything that determines them. A null for either digit
 * bound means the locale's own default (0 minimum, 3 maximum), which is what the
 * historical `new Intl.NumberFormat('en-US')` calls used.
 *
 * Intl rounds half away from zero by default, matching the ROUND_HALFUP the PHP
 * engine sets explicitly — the two must not drift.
 */
export const formatterFor = (tag, minDigits = null, maxDigits = null, grouping = true) => {
    const key = `${tag}|${minDigits ?? 'auto'}|${maxDigits ?? 'auto'}|${grouping ? 1 : 0}`;

    if (!formatterCache.has(key)) {
        const options = { useGrouping: grouping };

        if (minDigits !== null) {
            options.minimumFractionDigits = minDigits;
        }

        if (maxDigits !== null) {
            options.maximumFractionDigits = maxDigits;
        }

        formatterCache.set(key, new Intl.NumberFormat(icuLocale(tag), options));
    }

    return formatterCache.get(key);
};

/**
 * A grouped number. `digits` null keeps the locale's default precision (up to
 * three decimals), a number forces exactly that many decimal places.
 *
 * Note the default differs from the PHP CurrencyFormatService::decimal(), which
 * defaults to 0 because its callers (money(), thousands()) always render whole
 * numbers unless told otherwise. Here the null default is what reproduces the
 * historical bare `new Intl.NumberFormat('en-US')` calls.
 */
export const decimal = (value, digits = null) => formatterFor(locale(), digits, digits, true).format(value);

/**
 * A grouped number carrying AT MOST `maxDigits` decimals, with trailing zeros
 * dropped — for displays that show a decimal only when there is one (quantities,
 * percentages). `decimal()` above always pads to a fixed width.
 */
export const decimalUpTo = (value, maxDigits = 3) => formatterFor(locale(), 0, maxDigits, true).format(value);

/**
 * Format in an EXPLICIT locale rather than the page's own.
 *
 * Only for screens that legitimately show more than one currency at once — the
 * organisation summary lists projects that may each use a different one, and the
 * server sends the locale alongside each row. Everywhere else should use
 * decimal(), so figures follow the currency in scope.
 */
export const decimalIn = (tag, value, digits = null) => formatterFor(tag || locale(), digits, digits, true).format(value);

/**
 * The locale's group and decimal separator characters, derived from the
 * formatter itself rather than a lookup table — a table would be wrong for
 * some of the 104 currencies and would silently drift from CLDR.
 */
export const separators = (tag = locale()) => {
    const parts = formatterFor(tag, null, null, true).formatToParts(11111.1);

    return {
        group: parts.find((part) => part.type === 'group')?.value ?? '',
        decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    };
};

const PLAIN_NUMBER = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

// Pinned to en-US/latn rather than the active context: this one is not a
// display format but the canonical wire shape that gets handed back to a
// formatter, so it must never pick up a locale's separators or digits.
const CANONICAL_FORMATTER = new Intl.NumberFormat('en-US-u-nu-latn', { useGrouping: false });

/**
 * A number as a plain, locale-independent numeric string: no grouping, a dot
 * decimal, and no exponent at the magnitudes money reaches. Used for
 * intermediates that get handed back to a formatter, so they must not carry any
 * locale's separators.
 */
export const canonical = (value) => CANONICAL_FORMATTER.format(value);

/**
 * Canonicalise a NUMBER destined for a hidden submitted field: rounded to
 * `precision`, then formatted through canonical() so it carries no grouping and
 * a dot decimal whatever the page's locale.
 *
 * Rounding is Math.round on the scaled value, matching what call sites did
 * inline before this was extracted, so the stored figure is unchanged for
 * every input that already worked. That leaves halves rounding toward +∞
 * rather than away from zero (-1.005 gives -1, not -1.01); allowance fields
 * accept negatives, so this is preserved deliberately rather than "fixed" here.
 *
 * Takes a number, not a display string — a `.thousands-format` field's raw
 * value must go through parseLocaleNumber() first. Not a display formatter;
 * use decimal() or the formatters above for anything shown on screen.
 */
export const canonicalMoneyValue = (value, precision = 2) => {
    const numeric = Number.isFinite(value) ? value : 0;
    const factor = Math.pow(10, precision);

    return canonical(Math.round(numeric * factor) / factor);
};

/**
 * Strip a formatted value back to a canonical numeric STRING using the locale's
 * separators, preserving the digits exactly — "14.250.000,50" becomes
 * "14250000.50", not "14250000.5". Returns null when the input is not a plain
 * number.
 *
 * Deliberately DIFFERENT from the PHP CurrencyFormatService::normalise(), which
 * returns an already-canonical string untouched before doing any separator work.
 * That is not a drift: the two sides receive different things. This one reads
 * human keystrokes, where "1.234" typed on a TRY event means 1234, so the
 * grouped reading is correct. normalise() reads request data, which this layer
 * has already canonicalised, so there the canonical reading is correct. The
 * string is genuinely ambiguous; each side resolves it by what it is fed.
 */
export const unformat = (value, tag = locale()) => {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'number') {
        return isFinite(value) ? canonical(value) : null;
    }

    const raw = String(value).trim();

    if (raw === '') {
        return null;
    }

    const { group, decimal: decimalSeparator } = separators(tag);

    // Locales that group with a space use one of several invisible variants
    // (plain, no-break, narrow no-break); accept them all, but only for those
    // locales — stripping spaces under a locale that groups with something
    // else would change how a spaced value reads today.
    const SPACE_GROUPS = [' ', '\u00a0', '\u202f'];
    // CHF/de-CH groups with an apostrophe, and the browser's ICU (plain ASCII
    // U+0027) and PHP's ICU (typographic U+2019) disagree on which one to
    // emit, so a value rendered by either runtime can arrive here holding
    // either. Accept both, same rationale as the space variants.
    const APOSTROPHE_GROUPS = ["'", '\u2019'];
    const groupChars = SPACE_GROUPS.includes(group) ? SPACE_GROUPS : APOSTROPHE_GROUPS.includes(group) ? APOSTROPHE_GROUPS : [group];

    // Strip only GENUINE group separators: one must be followed by exactly three
    // digits and then another separator or the end of the number.
    //
    // Without that test a canonical value is destroyed by its own locale. On a
    // TRY event the group separator is "." — so "1234.56", which is what an
    // untouched Blade-rendered field value still holds, would have the "."
    // stripped as a group and submit 123456. No grouping convention produces a
    // two-digit group, so position tells the two apart unambiguously.
    let normalised = raw;
    groupChars.forEach((character) => {
        if (!character) {
            return;
        }
        // Separators are always single punctuation or space characters, so
        // backslash-escaping every non-word character is enough to make one
        // regex-safe.
        const quoted = character.replace(/[^\w]/g, (c) => '\\' + c);
        normalised = normalised.replace(new RegExp(`${quoted}(?=\\d{3}(?:${quoted}|\\D|$))`, 'g'), '');
    });

    if (decimalSeparator !== '.') {
        normalised = normalised.split(decimalSeparator).join('.');
    }

    return PLAIN_NUMBER.test(normalised) ? normalised : null;
};

/**
 * Read a number a human typed, using the locale's separators.
 *
 * Returns NaN for anything that is not a plain number, so callers can tell
 * "invalid" from a real value. Stripping separators blindly is the bug this
 * replaces: it turns a TRY user's "14,25" into 1425 and their "14.250.000,50"
 * into 14.25, then stores the result.
 */
export const parseLocaleNumber = (value, tag = locale()) => {
    if (typeof value === 'number') {
        return isFinite(value) ? value : NaN;
    }

    const normalised = unformat(value, tag);

    return normalised === null ? NaN : Number(normalised);
};

/**
 * An abbreviated headline figure. Scales and suffixes come from config, so INR
 * abbreviates in lakh/crore and TRY in billions at two decimal places, while
 * anything unmapped keeps the frozen "35.8M" / "696k" tile convention. The sign
 * is a leading minus, not the parentheses used on grids.
 */
export const compact = (value) => {
    const numeric = Number(value);

    if (!isFinite(numeric)) {
        return '0';
    }

    const sign = numeric < 0 ? '-' : '';
    const abs = Math.abs(numeric);
    const { suffixes, maximumFractionDigits } = resolveContext().compact;
    const scales = Object.keys(suffixes || {})
        .map(Number)
        .filter((scale) => scale > 0)
        .sort((a, b) => b - a);

    for (const scale of scales) {
        if (abs >= scale) {
            // Thousands render whole ("697k"); larger magnitudes carry one
            // decimal ("35.8M"). Trailing zeros are dropped either way, and
            // grouping stays off so the mantissa reads "14250M", not "14,250M".
            const digits = maximumFractionDigits ?? (scale === 1000 ? 0 : 1);
            const mantissa = formatterFor(locale(), 0, digits, false).format(abs / scale);

            return `${sign}${mantissa}${suffixes[scale]}`;
        }
    }

    return `${sign}${decimal(Math.round(abs), 0)}`;
};

/**
 * Attach the currency indicator to an already-formatted value, on the side and
 * with the spacing the currency calls for — TRY shows a trailing "TL", not a
 * leading glyph.
 */
export const indicator = (formatted) => {
    const { glyph, position, space } = resolveContext().indicator;

    if (!glyph) {
        return formatted;
    }

    const gap = space ? ' ' : '';

    return position === 'after' ? `${formatted}${gap}${glyph}` : `${glyph}${gap}${formatted}`;
};
